import { useRef, useState } from "react";
import { MoveHorizontal, Minus, Plus } from "lucide-react";
import { BEFORE, AFTER } from "@/data/content";
import { Reveal, Eyebrow } from "./Reveal";

const Slider = () => {
  const ref = useRef(null);
  const [pos, setPos] = useState(50);
  const [drag, setDrag] = useState(false);
  const update = (x) => {
    const r = ref.current.getBoundingClientRect();
    setPos(Math.min(100, Math.max(0, ((x - r.left) / r.width) * 100)));
  };
  return (
    <div
      ref={ref}
      data-testid="before-after-slider"
      className="relative aspect-[4/3] sm:aspect-[16/9] w-full select-none overflow-hidden rounded-[28px] border border-white/10 cursor-ew-resize touch-pan-y"
      onPointerDown={(e) => { setDrag(true); update(e.clientX); }}
      onPointerMove={(e) => drag && update(e.clientX)}
      onPointerUp={() => setDrag(false)}
      onPointerLeave={() => setDrag(false)}
    >
      <img src="/images/nachher.jpg" alt="Pool nach der Modernisierung" className="absolute inset-0 h-full w-full object-cover" draggable="false" />
      <img src="/images/vorher.jpg" alt="Alter Pool vor der Modernisierung" style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }} className="absolute inset-0 h-full w-full object-cover" draggable="false" />
      <span className="absolute left-4 top-4 rounded-full bg-[rgba(4,23,27,0.7)] px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.2em] text-[var(--signal)] backdrop-blur">Vorher</span>
      <span className="absolute right-4 top-4 rounded-full bg-[rgba(4,23,27,0.7)] px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.2em] text-[var(--lagoon)] backdrop-blur">Nachher</span>
      <div className="absolute inset-y-0 w-px bg-white/80" style={{ left: `${pos}%` }}>
        <div className="absolute top-1/2 left-1/2 flex h-14 w-14 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-[var(--foam)] text-[#04171B] shadow-2xl">
          <MoveHorizontal className="h-5 w-5" />
        </div>
      </div>
      <input
        type="range" min="0" max="100" value={pos} onChange={(e) => setPos(Number(e.target.value))}
        aria-label="Vorher-Nachher-Regler" data-testid="before-after-range" className="sr-only"
      />
    </div>
  );
};

const List = ({ title, items, tone, icon: Icon, testId }) => (
  <div data-testid={testId}>
    <p className={`font-mono text-xs uppercase tracking-[0.25em] ${tone}`}>{title}</p>
    <ul className="mt-5 space-y-3">
      {items.map((t) => (
        <li key={t} className="flex items-start gap-3 text-[var(--foam)]">
          <Icon className={`mt-1 h-4 w-4 shrink-0 ${tone}`} /> <span>{t}</span>
        </li>
      ))}
    </ul>
  </div>
);

export const BeforeAfter = () => (
  <section id="vorher-nachher" data-testid="before-after-section" className="mx-auto max-w-[1400px] px-5 py-24 sm:px-8 sm:py-32">
    <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
      <div>
        <Reveal><Eyebrow>Vorher / Nachher</Eyebrow></Reveal>
        <Reveal delay={0.08}>
          <h2 className="mt-6 max-w-3xl font-display text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1] tracking-[-0.03em]">Gleicher Pool. Ganz anderes Gefühl.</h2>
        </Reveal>
      </div>
      <Reveal delay={0.12}><p className="max-w-sm text-sm text-[var(--mist)]">Ziehen Sie den Regler. Visualisierung zur Veranschaulichung – kein Kundenprojekt.</p></Reveal>
    </div>
    <Reveal delay={0.1} className="mt-12"><Slider /></Reveal>
    <div className="mt-12 grid gap-10 sm:grid-cols-2">
      <Reveal><List testId="before-list" title="Alter Bestandspool" items={BEFORE} tone="text-[var(--signal)]" icon={Minus} /></Reveal>
      <Reveal delay={0.08}><List testId="after-list" title="Mit REVIVO-Smartpool" items={AFTER} tone="text-[var(--lagoon)]" icon={Plus} /></Reveal>
    </div>
  </section>
);
